import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelect from "components/common/StatusSelectWithChecks";
import { useDispatch, useSelector } from "react-redux";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as utils from "utils";

/** Steps through the submission's layers and shows which one is selected, using the same
 *  badge/name/status chip as the layer columns in IAB pricing. Selection is the shared active
 *  layer, so it stays in step with the other pricing tabs. Layers are added and removed on the
 *  Layer Pricing tab; this only moves between the ones that exist. */
const LayerNavigator = () => {
  const dispatch = useDispatch();
  const layers = useSelector(pricingSelectors.selectResolvedLayers) ?? [];
  const activeIndex = useSelector(pricingSelectors.selectActiveLayerIndex) ?? 0;
  const index = Math.min(activeIndex, Math.max(layers.length - 1, 0));
  const layer = layers[index];

  if (!layer) {
    return (
      <Typography variant={"body2"} color={"text.secondary"}>
        {"Add a layer on the Layer Pricing tab to price it."}
      </Typography>
    );
  }

  const step = (by) =>
    dispatch(
      pricingActions.updateActive((index + by + layers.length) % layers.length)
    );

  // shortLayerName renders a missing limit as "$null xs $0"; say what is actually wrong instead,
  // since a layer without a limit is the reason the model cannot price it
  const isIncomplete = layer.limit == null;
  const label = isIncomplete
    ? `Layer ${index + 1} — set a limit in Layer Pricing`
    : utils.shortLayerName(layer);

  return (
    <Stack direction={"row"} spacing={1} alignItems={"center"}>
      <IconButton
        size={"small"}
        aria-label={"Previous layer"}
        disabled={layers.length < 2}
        onClick={() => step(-1)}
      >
        <PrevIcon fontSize={"small"} />
      </IconButton>
      <Tooltip title={layer.status ?? ""}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusBadgeV2 status={layer.status} variant={"icon"} />
          <Typography
            fontSize={"0.8rem"}
            color={isIncomplete ? "text.secondary" : undefined}
            fontStyle={isIncomplete ? "italic" : undefined}
          >
            {label}
          </Typography>
          <StatusSelect
            layer={layer}
            variant={"switch"}
            status={layer.status ?? null}
            onChange={(status) =>
              dispatch(
                pricingActions.updateLayer({
                  layerId: layer.id,
                  values: { status },
                })
              )
            }
          />
        </Box>
      </Tooltip>
      <IconButton
        size={"small"}
        aria-label={"Next layer"}
        disabled={layers.length < 2}
        onClick={() => step(1)}
      >
        <NextIcon fontSize={"small"} />
      </IconButton>
      {layers.length > 1 && (
        <Typography variant={"caption"} color={"text.secondary"}>
          {`${index + 1} of ${layers.length}`}
        </Typography>
      )}
    </Stack>
  );
};

export default LayerNavigator;
